import { LitElement, html, css, classMap, styleMap } from '@hh/global.js'

class HHMegaMenuDrawer extends LitElement {
  static properties = {
    open: {
      type: Boolean,
      reflect: true,
    },
    currentMenu: {
      type: String,
      reflect: true,
      attribute: 'current-menu',
    },
    _scrollTop: {
      type: Number,
    },
    _currentFocusedMenu: {
      type: Object,
    },
    hoverDelay: {
      type: Number,
      attribute: 'hover-delay',
    },
  }

  constructor() {
    super()
    this.open = false
  }

  connectedCallback() {
    super.connectedCallback()
    this.overlay.forEach(el => {
      el.addEventListener('click', this.closeMegaMenu.bind(this))
    })
    window.addEventListener('triggerMegaMenu', (event) => {
      const mouseoverEvent = event.detail.mouseoverEvent
      let timer = null

      mouseoverEvent.target.addEventListener('mouseleave', () => {
        clearTimeout(timer)
      })

      timer = setTimeout(() => {
        this.openMegaMenu(mouseoverEvent)
      }, this.hoverDelay)
    })

    document.addEventListener('keydown', this.handleKeyDown.bind(this))
    this.toggleMenuButtons.forEach((button) => {
      button.addEventListener('keydown', this.handleToggleKeydown.bind(this))
    })
  }

  disconnectedCallback() {
    super.disconnectedCallback()
    this.overlay.forEach(el => {
      el.removeEventListener('click', this.closeMegaMenu)
    })
    window.removeEventListener('triggerMegaMenu', this.openMegaMenu)
    document.removeEventListener('keydown', this.handleKeyDown)
    this.toggleMenuButtons.forEach((button) => {
      button.removeEventListener('keydown', this.handleToggleKeydown.bind(this))
    })
  }

  firstUpdated() {
    setTimeout(() => {
      this.setTotalHeaderHeight()
      this.setDrawerHeight()
    }, 500)
  }

  updated(changedProperties) {
    if (changedProperties.has('open') && typeof changedProperties.get('open') !== 'undefined') {
      this.toggleOverlay()
      this.toggleBodyScroll()
      this.updateAriaHidden()
    }
    if (changedProperties.has('currentMenu')) {
      this.setCurrentMenu(this.currentMenu)
    }
  }

  updateAriaHidden() {
    const isHidden = !this.open;

    this.setAttribute('aria-hidden', String(isHidden));

    if (isHidden) {
      this.setAttribute('inert', '');
    } else {
      this.removeAttribute('inert');
    }
  }

  handleToggleKeydown(e) {
    if (e.code === 'Enter' || e.code === 'Space') {
      e.preventDefault()
      this.openMegaMenu(e)
    }
  }

  // Open and close methods
  openMegaMenu(e) {
    const target = e.target.closest('[data-menu-handle]') ?? e.target
    this._currentFocusedMenu = target
    const { menuHandle } = target.dataset
    this.open = true
    this.currentMenu = menuHandle
    this.setTotalHeaderHeight()
    this.setDrawerHeight()

    this.dispatchEvent(new CustomEvent('mega-menu-open', { bubbles: true }))
  }

  closeMegaMenu() {
    this.open = false
    this.removeTrapFocus()
    this.currentMenu = ''
  }

  handleKeyDown(e) {
    if (e.key === 'Tab' || e.keyCode === 9) this.revertHideFocus()
    if (e.key !== 'Escape') return
    this.closeMegaMenu()
  }

  revertHideFocus() {
    this.classList.remove('hide-focus')
    const buttonElement =
      this._currentFocusedMenu?.shadowRoot?.querySelector('button')
    buttonElement?.classList.remove('hide-focus')
  }

  toggleOverlay() {
    this.overlay.forEach(el => {
      el.classList[this.open ? 'add' : 'remove']('active')
    })
  }

  toggleBodyScroll() {
    if (this.open) {
      this._scrollTop = window.scrollY
    }

    document.body.classList[this.open ? 'add' : 'remove']('no-scroll')

    if (!this.open) {
      window.scrollTo(0, this._scrollTop)
      setTimeout(() => {
        this.stickyHeader.externalRevealHeader()
      }, 0)
    } else {
      document.body.scrollTo(0, this._scrollTop)
    }
  }

  setCurrentMenu(menuHandle) {
    this.submenus.forEach((menu) => {
      const megaMenuHandle = menu.dataset.megaMenuHandle
      menu.classList[megaMenuHandle === menuHandle ? 'add' : 'remove']('active')

      if (megaMenuHandle === menuHandle) {
        this.toggleNegativeTabIndex(menu)
        this.addFocusTrap(menu)
      }
    })

    this.navLinks.forEach((link) => {
      const linkMenuHandle = link.dataset.menuHandle
      link.classList[linkMenuHandle === menuHandle ? 'add' : 'remove']('active')
      link.setAttribute('aria-expanded', linkMenuHandle === menuHandle)
    })
  }

  toggleNegativeTabIndex(menu) {
    const links = this.querySelectorAll('.js-submenu-link')
    links.forEach((link) => {
      if (menu.contains(link)) {
        link.removeAttribute('tabindex')
        link.removeAttribute('aria-hidden')
      } else {
        link.setAttribute('tabindex', '-1')
        link.setAttribute('aria-hidden', 'true')
      }
    })
  }

  addFocusTrap(menu) {
    this.classList.add('hide-focus')
    const focusableElementsString =
      'a, button, input, select, textarea, [tabindex]:not([tabindex="-1"])'

    this.focusableDomElements = this.querySelectorAll(focusableElementsString)
    this.focusableDomElements[0].classList.add('focus-visible')
    this.focusablePromoElements =
      this.featuredPromos?.shadowRoot.querySelectorAll(focusableElementsString) ?? []

    this.closeButton = this.shadowRoot.querySelector('.close')
    this.focusableFeaturedContentElements =
      this.featuredContent?.querySelectorAll(focusableElementsString) ?? []

    this.focusableElements = [
      ...this.focusableDomElements,
      ...this.focusablePromoElements,
      ...this.focusableFeaturedContentElements,
      this.closeButton,
    ]

    this.firstFocusableElement = this.focusableElements[0]
    this.lastFocusableElement =
      this.focusableElements[this.focusableElements.length - 1]

    setTimeout(() => {
      this.firstFocusableElement.focus()
    })
    this.currentMenuElement = menu

    this.addEventListener('keydown', this.onTrapFocus.bind(this))
  }

  removeTrapFocus() {
    this.removeEventListener('keydown', this.onTrapFocus)
    const buttonElement =
      this._currentFocusedMenu?.shadowRoot?.querySelector('button')
    buttonElement?.focus()
    buttonElement?.classList.add('hide-focus')
  }

  onTrapFocus(e) {
    const isTabPressed = e.key === 'Tab' || e.keyCode === 9
    if (!isTabPressed || !this.open) return

    if (e.shiftKey) {
      if (
        document.activeElement === this.firstFocusableElement ||
        document.activeElement.shadowRoot?.activeElement ===
        this.firstFocusableElement
      ) {
        this.lastFocusableElement.focus()
        e.preventDefault()
      }
    } else {
      if (
        document.activeElement === this.lastFocusableElement ||
        document.activeElement.shadowRoot?.activeElement ===
        this.lastFocusableElement
      ) {
        this.firstFocusableElement.focus()
        e.preventDefault()
      }
    }
  }

  setTotalHeaderHeight() {
    HH.setCSSVariable('--hh-header-height', `${this.header.offsetHeight}px`)
    HH.setCSSVariable('--hh-announcement-bar-height', `${this.announcementBar.offsetHeight}px`)
    HH.setCSSVariable('--hh-total-header-height', `${this.totalHeaderHeight}px`)
  }

  setDrawerHeight() {
    HH.setCSSVariable('--hh-mega-menu-drawer-height', `${this.windowHeight}px`)
  }

  render() {
    return html`
      <div class="wrapper">
        <div class="menus-wrapper">
          <slot name="submenu"></slot>
        </div>
        <slot name="promos"></slot>
        <slot name="featured-content"></slot>
      </div>
      <button
        class="${classMap({
      close: true,
      active: this.open,
    })}"
        @click=${this.closeMegaMenu}
      >
        Close
      </button>
      <slot name="overlay"></slot>
    `
  }

  static styles = css`
    :host {
      box-sizing: border-box;
    }

    *,
    *::before,
    *::after {
      box-sizing: inherit;
    }

    .wrapper {
      position: relative;
      z-index: 0;
      padding: calc(var(--hh-total-header-height) + 10px) 32px;
      height: 100%;
      width: 55%;
      max-width: 596px;
      box-sizing: border-box;
      display: flex;
      flex-direction: column;
      background-color: var(--hh-color-background);
    }

    .menus-wrapper {
      position: relative;
      height: 100%;
    }

    .close {
      opacity: 0;
      color: #1C1C1C;
      padding: 8px 12px;
      position: absolute;
      top: 16px;
      right: 16px;
      border: 0;
      border-radius: 16px;
      background: #F3F3F3;
      font: var(--hh-button-font);
      font-size: 14px;
      color: var(--hh-color-night);
      cursor: pointer;
      line-height: 100%;
      letter-spacing: .2px;
    }

    .close.active {
      opacity: 1;
    }

    .close:focus-visible {
      outline: 0.1250rem solid var(--hh-color-focus);
      outline-offset: var(--hh-focus-outline-offset);
    }
  `

  // Getters
  get windowHeight() {
    return window.innerHeight
  }

  get totalHeaderHeight() {
    return this.header.offsetHeight + this.announcementBar.offsetHeight
  }

  get header() {
    return document.querySelector('.js-header') ?? null
  }

  get announcementBar() {
    return document.querySelector('.js-announcement-bar') ?? null
  }

  get overlay() {
    return document.querySelectorAll('.js-mega-menu-overlay') ?? null
  }

  get submenus() {
    const slot = this.shadowRoot.querySelector('slot[name="submenu"]')
    return slot.assignedElements({ flatten: true }) ?? null
  }

  get stickyHeader() {
    return document.querySelector('hh-sticky-header')
  }

  get navLinks() {
    const navLinks = document.querySelectorAll('[data-menu-handle]')
    if (!navLinks) return null
    return navLinks
  }

  get featuredPromos() {
    return document.querySelector('hh-menu-featured-promos') ?? null
  }

  get featuredContent() {
    return document.querySelector('.js-menu-featured-content') ?? null
  }

  get toggleMenuButtons() {
    const toggleButtons = document.querySelectorAll('.js-toggle-menu-button')
    if (!toggleButtons) return null
    return toggleButtons
  }
}

customElements.define('hh-mega-menu-drawer', HHMegaMenuDrawer)
